import type { TFunction } from "i18next";

import type { ReviewFilters } from "../types/review";

export type AppliedReviewFilters = Omit<ReviewFilters, "start_time" | "end_time">;

export interface ReviewFilterSnapshot {
  selectedLocationId?: number;
  selectedMachineIds: number[];
  dateRange: { fromDate: string; toDate: string };
  appliedFilters: AppliedReviewFilters;
}

export interface SavedPreset {
  id: string;
  name: string;
  snapshot: ReviewFilterSnapshot;
  createdAt: string;
}

export const MAX_HISTORY = 10;

/**
 * Stable stringify — sorts object keys so key order doesn't affect equality,
 * and sorts the contents of all-primitive arrays so element order doesn't
 * either. Snapshots only hold primitive arrays (`selectedMachineIds: number[]`),
 * so two snapshots that differ only in the order of that array compare equal.
 * Non-primitive arrays are left untouched, and the input is never mutated (we
 * sort a copy).
 */
const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, val) => {
    if (Array.isArray(val)) {
      const allPrimitive = val.every(
        (el) => typeof el === "string" || typeof el === "number",
      );
      if (!allPrimitive) return val;
      return [...val].sort((a, b) =>
        typeof a === "number" && typeof b === "number"
          ? a - b
          : String(a) < String(b)
            ? -1
            : String(a) > String(b)
              ? 1
              : 0,
      );
    }
    if (val && typeof val === "object") {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = (val as Record<string, unknown>)[k];
          return acc;
        }, {});
    }
    return val;
  });

export const snapshotsEqual = (
  a: ReviewFilterSnapshot,
  b: ReviewFilterSnapshot,
): boolean => stableStringify(a) === stableStringify(b);

/** Prepend a snapshot, dedupe an equal one, cap the list length. */
export const pushHistory = (
  history: ReviewFilterSnapshot[],
  snapshot: ReviewFilterSnapshot,
  cap: number = MAX_HISTORY,
): ReviewFilterSnapshot[] => {
  const deduped = history.filter((h) => !snapshotsEqual(h, snapshot));
  return [snapshot, ...deduped].slice(0, cap);
};

/**
 * Short human label for a snapshot row in the Presets menu.
 *
 * The filter values themselves (`label_in`, `review_state_in`, …) are wire
 * values the backend matches on, so they are shown raw; only the words around
 * them are translated, at the render edge via the passed `t`.
 */
export const summariseSnapshot = (
  s: ReviewFilterSnapshot,
  t: TFunction<"mlExports">,
): string => {
  const parts: string[] = [];
  if (s.selectedMachineIds.length) {
    parts.push(
      t("presets.summary.machines", {
        count: s.selectedMachineIds.length,
        value: s.selectedMachineIds.length,
      }),
    );
  } else if (s.selectedLocationId) {
    parts.push(t("presets.summary.location"));
  }
  const f = s.appliedFilters;
  if (f.label_in) parts.push(t("presets.summary.label", { value: f.label_in }));
  if (f.review_state_in)
    parts.push(t("presets.summary.review", { value: f.review_state_in }));
  if (f.event_state_in)
    parts.push(t("presets.summary.event", { value: f.event_state_in }));
  if (f.review_presence)
    parts.push(
      f.review_presence === "reviewed"
        ? t("presets.summary.reviewed")
        : t("presets.summary.unreviewed"),
    );
  if (f.min_confidence != null || f.max_confidence != null)
    parts.push(t("presets.summary.confidence"));
  if (f.reviewed_by) parts.push(t("presets.summary.reviewer"));
  if (f.reviewed_after || f.reviewed_before)
    parts.push(t("presets.summary.reviewedIn"));
  if (f.validity_state_in)
    parts.push(t("presets.summary.validity", { value: f.validity_state_in }));
  if (f.require_valid_flag) parts.push(t("presets.summary.validOnly"));
  return parts.length ? parts.join(", ") : t("presets.summary.none");
};
